import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { Avatar } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import { Button, Input, Pagination, Select } from "../../../shared";
import DataTableUntyped from "../../../shared/dashboards/DataTable/DataTable";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { buildUserManagerUrl } from "../../../../utils/NavigationUtils";
import { convertToUserTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import { useAffiliateUsers } from "../../../../queries/affiliates/useAffiliateUsers";
import { useAffiliatePromoCodes } from "../../../../queries/affiliates/useAffiliatePromoCodes";
import type { AffiliateAnalyticsDataSource } from "../../../../queries/affiliates/analyticsDataSource";
import type { ShareableColumnId } from "../../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import type {
  AffiliateUserDetail,
  AffiliateUsersFtdFilter,
} from "../../../../types/affiliates";

import styles from "./AffiliateUsers.module.css";

type AffiliateUsersTableProps = {
  columns: Array<Record<string, unknown>>;
  dataSource: AffiliateUserRow[];
  rowKey: string;
  scrollPreset?: string;
  loading?: boolean;
  pagination?: false | Record<string, unknown>;
};
const TypedDataTable =
  DataTableUntyped as unknown as FC<AffiliateUsersTableProps>;

export type AffiliateUsersProps = {
  affiliateId?: string;
  // ISO YYYY-MM-DD range from the AffiliateProfile header date picker.
  // Falls back to month-to-date when not supplied.
  startDate?: string;
  endDate?: string;
  // "portal" runs against the affiliate-portal endpoint (token-scoped, no
  // affiliateId needed).
  dataSource?: AffiliateAnalyticsDataSource;
  // portal_view_config selection. Gates the columns this table shares with
  // the promo-codes config: `referralCode` → Promo Code, `totalFtds` → FTD?,
  // `totalRevenue` → Total Deposits + Total Withdrawals.
  // Undefined → all columns (operator profile default).
  selectedColumns?: ReadonlyArray<ShareableColumnId>;
};

type AffiliateUserRow = {
  key: string;
  userId: string;
  username: string;
  profilePhoto: string | null;
  promoCode: string;
  registrationState: string;
  dateJoined: string | null;
  ftd: boolean;
  totalDeposits: number;
  totalWithdrawals: number;
};

// Modeled after the main Users table: pick a field, then type a value.
const SEARCH_BY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "username", label: AppStrings.SEARCH_BY_USERNAME },
  { value: "email", label: AppStrings.SEARCH_BY_EMAIL },
  { value: "user_id", label: AppStrings.SEARCH_BY_USER_ID },
];

const FTD_FILTER_OPTIONS: Array<{
  value: AffiliateUsersFtdFilter;
  label: string;
}> = [
  { value: "yes", label: AppStrings.YES },
  { value: "no", label: AppStrings.NO },
];

const toRow = (
  affiliateId: string,
  raw: AffiliateUserDetail,
): AffiliateUserRow => ({
  key: `${affiliateId}:${raw.user_id}`,
  userId: raw.user_id,
  username: raw.username ?? raw.user_id,
  profilePhoto: raw.profile_photo ?? null,
  promoCode: raw.promo_code ?? "—",
  registrationState: raw.registration_state ?? "—",
  dateJoined: raw.date_joined,
  ftd: Boolean(raw.ftd),
  totalDeposits: typeof raw.total_deposits === "number" ? raw.total_deposits : 0,
  totalWithdrawals:
    typeof raw.total_withdrawals === "number" ? raw.total_withdrawals : 0,
});

const formatInteger = (value: number): string =>
  Number.isFinite(value) ? value.toLocaleString("en-US") : "0";

const renderUsernameCell = (row: AffiliateUserRow): ReactNode => {
  const initial = (row.username || row.userId).charAt(0).toUpperCase();
  return (
    <a
      className={styles.usernameCell}
      href={buildUserManagerUrl(row.userId)}
      target="_blank"
      rel="noreferrer"
      data-testid={`affiliate-users-username-${row.userId}`}
    >
      <Avatar
        size={28}
        src={row.profilePhoto ?? undefined}
        style={{ backgroundColor: "var(--COLOR-PRIMARY)", flexShrink: 0 }}
      >
        {initial}
      </Avatar>
      <span className={styles.usernameText}>{row.username}</span>
    </a>
  );
};

const AffiliateUsers: FC<AffiliateUsersProps> = ({
  affiliateId,
  startDate,
  endDate,
  dataSource = "operator",
  selectedColumns,
}) => {
  const [searchBy, setSearchBy] = useState<string>("username");
  const [searchValue, setSearchValue] = useState("");
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const [promoCodeFilter, setPromoCodeFilter] = useState<string | undefined>(
    undefined,
  );
  const [ftdFilter, setFtdFilter] = useState<
    AffiliateUsersFtdFilter | undefined
  >(undefined);
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cursorStack, setCursorStack] = useState<(string | null | undefined)[]>(
    [undefined],
  );

  const debouncedSearch = useDebouncedValue(searchValue);

  // The search-by pick only matters once there's a term — keeping the idle
  // value out of the query key avoids a pointless refetch + page reset.
  const effectiveSearchField = debouncedSearch ? searchBy : undefined;

  // Any filter change must reset to page 1 — keeping a deep-page cursor alive
  // across filter changes would point into the previous result set.
  useEffect(() => {
    setCurrentPage(1);
    setCursorStack((prev) =>
      prev.length === 1 && prev[0] === undefined ? prev : [undefined],
    );
  }, [
    effectiveSearchField,
    debouncedSearch,
    promoCodeFilter,
    ftdFilter,
    stateFilter,
    pageSize,
    startDate,
    endDate,
  ]);

  const cursorParam =
    currentPage <= 1 ? undefined : (cursorStack[currentPage - 1] ?? undefined);

  const dateRange = useMemo(
    () => ({
      start_date: startDate ?? getAdminNow().startOf("month").format("YYYY-MM-DD"),
      end_date: endDate ?? getAdminNow().format("YYYY-MM-DD"),
    }),
    [startDate, endDate],
  );

  const usersQuery = useAffiliateUsers(
    affiliateId,
    {
      search_field: effectiveSearchField,
      search: debouncedSearch || undefined,
      limit: pageSize,
      cursor: cursorParam ?? undefined,
      promo_code: promoCodeFilter,
      ftd: ftdFilter,
      registration_state: stateFilter,
      ...dateRange,
    },
    dataSource,
  );

  // Promo Code filter options come from the affiliate's own promo codes.
  const promoCodesQuery = useAffiliatePromoCodes(affiliateId, dateRange);
  const promoCodeOptions = useMemo(
    () =>
      (promoCodesQuery.data?.items ?? []).map((code) => ({
        value: code.promo_code,
        label: code.promo_code,
      })),
    [promoCodesQuery.data?.items],
  );

  // Cursor-based BE returns no count, so synthesize a total and bump by 1
  // while a next cursor exists to keep the Next button enabled.
  const fakeTotal = useMemo(() => {
    const itemsThisPage = usersQuery.data?.items.length ?? 0;
    const hasNext = Boolean(usersQuery.data?.cursor);
    return hasNext
      ? currentPage * pageSize + 1
      : (currentPage - 1) * pageSize + itemsThisPage;
  }, [usersQuery.data, currentPage, pageSize]);

  const nextCursorFromResponse = usersQuery.data
    ? (usersQuery.data.cursor ?? null)
    : undefined;
  useEffect(() => {
    if (nextCursorFromResponse === undefined) return;
    setCursorStack((prev) => {
      const existing = prev[currentPage] ?? null;
      if (existing === nextCursorFromResponse) return prev;
      const next = [...prev];
      while (next.length <= currentPage) next.push(undefined);
      next[currentPage] = nextCursorFromResponse;
      return next;
    });
  }, [currentPage, nextCursorFromResponse]);

  const usersData = useMemo<AffiliateUserRow[]>(
    () =>
      (usersQuery.data?.items ?? []).map((raw) =>
        toRow(affiliateId ?? "", raw),
      ),
    [usersQuery.data?.items, affiliateId],
  );

  // Registration State options are derived from the visible page until BE
  // ships filter metadata — states only on other pages won't show up.
  const registrationStateOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const row of usersData) {
      if (row.registrationState && row.registrationState !== "—") {
        seen.add(row.registrationState);
      }
    }
    return Array.from(seen)
      .sort()
      .map((state) => ({ value: state, label: state }));
  }, [usersData]);

  // Undefined selection → every column (operator profile). With a selection,
  // only the shared toggles gate: `referralCode` and `totalFtds`.
  const showSharedColumn = (id: ShareableColumnId): boolean =>
    !selectedColumns || selectedColumns.includes(id);

  const columns = useMemo<Array<Record<string, unknown>>>(
    () => [
      {
        title: AppStrings.USER_ID,
        dataIndex: "userId",
        key: "userId",
        width: 120,
      },
      {
        title: AppStrings.USERNAME,
        dataIndex: "username",
        key: "username",
        width: 180,
        render: (_value: unknown, row: AffiliateUserRow) =>
          renderUsernameCell(row),
      },
      ...(showSharedColumn("referralCode")
        ? [
            {
              title: AppStrings.PROMO_CODE,
              dataIndex: "promoCode",
              key: "promoCode",
              width: 140,
            },
          ]
        : []),
      {
        title: AppStrings.REGISTRATION_STATE,
        dataIndex: "registrationState",
        key: "registrationState",
        width: 160,
      },
      {
        title: AppStrings.DATE_JOINED,
        dataIndex: "dateJoined",
        key: "dateJoined",
        width: 140,
        render: (value: string | null) =>
          // Athena UTC naive `"YYYY-MM-DD HH:mm:ss"` (or date-only) — convert.
          value ? convertToUserTimezone(value, "D MMM, YYYY") : "—",
      },
      ...(showSharedColumn("totalFtds")
        ? [
            {
              title: AppStrings.FTD_QUESTION,
              dataIndex: "ftd",
              key: "ftd",
              width: 90,
              render: (value: boolean) =>
                value ? AppStrings.YES : AppStrings.NO,
            },
          ]
        : []),
      // Deposits / withdrawals are the user-level breakdown of revenue, so
      // they follow the Total Revenue toggle. No selection (operator
      // profile) → always on; values the BE strips per its access mode
      // just arrive as 0.
      ...(showSharedColumn("totalRevenue")
        ? [
            {
              title: AppStrings.TOTAL_DEPOSITS,
              dataIndex: "totalDeposits",
              key: "totalDeposits",
              width: 140,
              render: (value: number) => formatInteger(value),
            },
            {
              title: AppStrings.TOTAL_WITHDRAWALS,
              dataIndex: "totalWithdrawals",
              key: "totalWithdrawals",
              width: 160,
              render: (value: number) => formatInteger(value),
            },
          ]
        : []),
    ],
    // showSharedColumn is derived from selectedColumns — keep deps on data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedColumns],
  );

  const searchPlaceholder =
    SEARCH_BY_OPTIONS.find((option) => option.value === searchBy)?.label ??
    AppStrings.SEARCH;

  return (
    <section
      className={styles.container}
      data-testid="affiliate-users"
      data-affiliate-id={affiliateId ?? ""}
    >
      <div className={styles.tableSection}>
        <h2 className={styles.tableTitle}>{AppStrings.USERS}</h2>

        <div className={styles.searchBar}>
          <Select<string>
            className={styles.searchBySelect}
            value={searchBy}
            onChange={(value) => setSearchBy(value)}
            options={SEARCH_BY_OPTIONS}
            data-testid="affiliate-users-search-by"
          />
          <div className={styles.searchInputWrapper}>
            <Input
              className={styles.searchInput}
              placeholder={searchPlaceholder}
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              allowClear
              data-testid="affiliate-users-search"
            />
          </div>

          <div className={styles.divider} />

          <Button
            intent="primary"
            variant="solid"
            data-testid="affiliate-users-filters-toggle"
            aria-label={AppStrings.ADDITIONAL_FILTERS}
            aria-expanded={showExtraFilters}
            iconLeft={<Filter size={16} />}
            onClick={() => setShowExtraFilters((prev) => !prev)}
          >
            {showExtraFilters ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </Button>
        </div>

        <div
          className={styles.filtersRow}
          data-testid="affiliate-users-filters-panel"
          style={{ display: showExtraFilters ? "grid" : "none" }}
        >
          <Select<string>
            className={styles.filterSelect}
            placeholder={AppStrings.PROMO_CODE}
            value={promoCodeFilter}
            onChange={(value) => setPromoCodeFilter(value)}
            allowClear
            options={promoCodeOptions}
            data-testid="affiliate-users-filter-promo-code"
          />
          <Select<AffiliateUsersFtdFilter>
            className={styles.filterSelect}
            placeholder={AppStrings.FTD_FILTER_LABEL}
            value={ftdFilter}
            onChange={(value) => setFtdFilter(value)}
            allowClear
            options={FTD_FILTER_OPTIONS}
            data-testid="affiliate-users-filter-ftd"
          />
          <Select<string>
            className={styles.filterSelect}
            placeholder={AppStrings.REGISTRATION_STATE}
            value={stateFilter}
            onChange={(value) => setStateFilter(value)}
            allowClear
            options={registrationStateOptions}
            data-testid="affiliate-users-filter-state"
          />
        </div>

        <TypedDataTable
          rowKey="key"
          columns={columns}
          dataSource={usersData}
          scrollPreset="ultraWide"
          loading={usersQuery.isLoading || usersQuery.isFetching}
          pagination={false}
        />
        <Pagination
          total={fakeTotal}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageSizeChange={(value: number) => {
            setCurrentPage(1);
            setPageSize(value);
          }}
          onPageChange={(nextPage: number) => {
            if (nextPage < currentPage) {
              setCurrentPage(nextPage);
              return;
            }
            if (nextPage === currentPage) return;
            if (usersQuery.data?.cursor) {
              setCurrentPage(nextPage);
            }
          }}
          itemLabel={AppStrings.USERS_ITEM_LABEL}
          restrictToAdjacentPages
        />
      </div>
    </section>
  );
};

export default AffiliateUsers;
